import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const WarehouseEntryForm = () => {
  const [formData, setFormData] = useState({
    purchase_contract_id: '',
    cma_id: '',
    warehouse_id: '',
    commodity_id: '',
    received_date: '',
    received_qty_mt: '',
    qc_status: '',
    grade: '',
    unit_cost_inr: ''
  });
  const [warehouses, setWarehouses] = useState([]);
  const [cmaList, setCmaList] = useState([]);
  const [importOrders, setImportOrders] = useState([]);
  const [commodityOptions, setCommodityOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [quantityError, setQuantityError] = useState('');
  const navigate = useNavigate();

  // Fetch CMA list and import orders for dropdown
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch CMA list
        const cmaRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-cma-list`, {
          method: 'GET',
          credentials: 'include',
        });
        if (cmaRes.ok) {
          const cmaData = await cmaRes.json();
          setCmaList(cmaData.data || []);
        }

        // Fetch import orders
        const importRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-purchase-contract`, {
          method: 'GET',
          credentials: 'include',
        });
        if (importRes.ok) {
          const importData = await importRes.json();
          setImportOrders(importData.data || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);
useEffect(() => {
  const fetchCommodities = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, {
        method: 'GET',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setCommodityOptions(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch commodities:', error);
      setCommodityOptions([]);
    }
  };

  fetchCommodities();
}, []);

  // Fetch warehouses when CMA changes
  useEffect(() => {
    if (!formData.cma_id) {
      setWarehouses([]);
      setFormData(prev => ({ ...prev, warehouse_id: '' }));
      return;
    }
    
    const fetchWarehouses = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cma_id=${formData.cma_id}`, {
         
          method: 'GET',
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setWarehouses(data.data || []);
          setFormData(prev => ({ ...prev, warehouse_id: '' })); // clear warehouse selection
        }
      } catch (error) {
        setWarehouses([]);
        setFormData(prev => ({ ...prev, warehouse_id: '' }));
      }
    };
    fetchWarehouses();
  }, [formData.cma_id]);

  const validateQuantity = (quantity) => {
    if (selectedContract && quantity && parseFloat(quantity) > parseFloat(selectedContract.remaining_qty_mt)) {
      setQuantityError(`Quantity cannot exceed available contract limit (${parseFloat(selectedContract.remaining_qty_mt).toFixed(3)} MT)`);
    } else {
      setQuantityError('');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'received_qty_mt') {
      validateQuantity(value);
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'purchase_contract_id') {
      const selected = importOrders.find(o => (o.id || o.purchase_contract_id) === value);
      setSelectedContract(selected || null);
      setQuantityError('');
      
      // Auto-select commodity if present
      if (selected && selected.commodity_id) {
        setFormData(prev => ({ ...prev, commodity_id: selected.commodity_id }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (quantityError) {
      setMessage({ type: 'error', text: 'Please fix the quantity error before submitting' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/warehouse/warehouse-entry-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Warehouse Entry Created Successfully!' });
        setFormData({
          purchase_contract_id: '',
          cma_id: '',
          warehouse_id: '',
          commodity_id: '',
          received_date: '',
          received_qty_mt: '',
          qc_status: '',
          grade: '',
          unit_cost_inr: ''
        });
      } else {
        setMessage({ type: 'error', text: data.message || 'Something went wrong' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server Error' });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <div className="flex justify-between items-center border-b pb-3 mb-5">
        <h2 className="text-[15px] font-medium text-gray-800">Warehouse Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-3 py-1 text-[12px] rounded font-normal hover:bg-[#166534] hover:text-white hover:border-[#166534] transition"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
  <label className="block text-[13px] text-gray-700 mb-1">Purchase Contract</label>
  <SearchableDropdown
    options={importOrders.map(order => ({
      id: order.id || order.purchase_contract_id,
      name: `${order.purchase_contract_no || 'N/A'} (${order.vessel_name || 'N/A'}) - ${order.remaining_qty_mt !== undefined ? parseFloat(order.remaining_qty_mt).toFixed(3) + ' MT Avail' : ''}`
    }))}
    value={formData.purchase_contract_id}
    onChange={(value) => handleDropdownChange('purchase_contract_id', value)}
    placeholder="Select Purchase Contract"
    displayKey="name"
    valueKey="id"
    required
  />
</div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">CMA</label>
            <SearchableDropdown
              options={cmaList.map(cma => ({
                id: cma.cma_id,
                name: cma.name
              }))}
              value={formData.cma_id}
              onChange={(value) => handleDropdownChange('cma_id', value)}
              placeholder="Select CMA"
              displayKey="name"
              valueKey="id"
              required
            />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Warehouse</label>
            <SearchableDropdown
              options={warehouses.map(warehouse => ({
                id: warehouse.warehouse_id,
                name: `${warehouse.name}`
              }))}
              value={formData.warehouse_id}
              onChange={(value) => handleDropdownChange('warehouse_id', value)}
              placeholder="Select Warehouse"
              displayKey="name"
              valueKey="id"
              required
            />
          </div>
           <div>
            <label className="block text-[12px] font-medium mb-1">Commodity</label>
            <SearchableDropdown
              options={commodityOptions}
              value={formData.commodity_id}
              onChange={value => handleDropdownChange('commodity_id', value)}
              placeholder="Select Commodity"
              displayKey="name"
              valueKey="commodity_id"
              required
              customDisplay={(item) => `${item.name} (${item.origin || 'N/A'})`}
            />
          </div>

          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Received Date</label>
            <input 
              type="date" 
              name="received_date" 
              value={formData.received_date} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              required 
              autoComplete="off" 
            />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Received Quantity (MT)</label>
            <input 
              type="number" 
              step="0.001"
              name="received_qty_mt" 
              value={formData.received_qty_mt} 
              onChange={handleChange} 
              className={`w-full border px-3 py-1.5 text-[13px] bg-white focus:outline-none rounded-none ${
                quantityError ? 'border-red-500' : 'border-gray-300 focus:border-blue-400'
              }`}
              required 
              autoComplete="off" 
            />
            {quantityError && (
              <div className="text-red-500 text-[12px] mt-1">{quantityError}</div>
            )}
            {selectedContract && !quantityError && (
              <div className="text-gray-500 text-[12px] mt-1">
                Available to receive: {parseFloat(selectedContract.remaining_qty_mt).toFixed(3)} MT
              </div>
            )}
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">QC Status</label>
            <select 
              name="qc_status" 
              value={formData.qc_status} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              required
            >
              <option value="">Select QC Status</option>
              <option value="PASSED">PASSED</option>
              <option value="HOLD">HOLD</option>
              <option value="REJECTED">REJECTED</option>
            </select>
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Grade</label>
            <input 
              type="text" 
              name="grade" 
              value={formData.grade} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              required 
              autoComplete="off" 
            />
          </div>

          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Unit Cost (INR)</label>
            <input 
              type="number" 
              step="0.01"
              name="unit_cost_inr" 
              value={formData.unit_cost_inr} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              autoComplete="off" 
            />
          </div>
        </div>
        <div className="pt-2 flex items-center gap-3">
          <button 
            type="submit" 
            className="px-3 py-1.5 text-[12px] bg-[#15803d] text-white font-normal rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60" 
            style={{ minWidth: '110px' }} 
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Entry'}
          </button>
          {message && (
            <div className={`text-[13px] ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default WarehouseEntryForm;
